import { AddressType, AddressUse, ContactPointSystem, ContactPointUse, NameUse, WorkflowContentPromptType } from "@/store/enums";
import { MAX_32_BIT_SIGNED_INT } from "@/utils";
import { DateTime } from "luxon";
import { z } from "zod";
import { globalT } from '@/config/i18n';

const t = (key: string, value?: string) => globalT(key, value ? [value] : []);

export const BLANK_IMG = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

export const OptionalStringSchema = z.string().trim().nullish().or(z.literal(''));
export const OptionalDBStringSchema = z.string().trim().max(255).nullish().or(z.literal(''));
export const RequiredDBStringSchema = z.string().trim().min(1).max(255);
export const RequiredIdSchema = z.number().nonnegative({ message: t('message.validation.required') });
export const RequiredStringSchema = z.string().trim().min(1);
export const DropDownValueSchema = z.number().optional().refine(x => x !== undefined, {message: t('message.validation.fieldRequired')})
export const PasswordSchema = z.string().trim().nullish().or(z.literal(''))
    .refine(x => !x || (!!x && /^(?=.*\d)(?=.*[a-zA-Z])[\S]{8,32}$/.test(x)), {
        message: t('message.validation.passwordRequirement')
    });
export const RequiredEmailSchema = z.string()
    .email({ message: "Invalid Email Address" })
    .max(255, { message: "Email Address must be less than 255 characters" })
    .refine(value => value.trim().length > 0, {
        message: "Email Address is required",
    })
    
export const TimeSchema = OptionalStringSchema.refine(x => !x || DateTime.fromISO(x).isValid, {message: t('message.validation.invalidTime')})

export const DateSchema = OptionalStringSchema
    .refine(x => !x || DateTime.fromISO(x).isValid, {
        message: t('message.validation.invalidDate')
    })

export const RequiredDateSchema = RequiredStringSchema
    .refine(x => !x || DateTime.fromISO(x).isValid, {
        message: t('message.validation.invalidDate')
    })

const testName = (str: string | null | undefined) => !!str && /^[\w\s',.-]+$/g.test(str);
export const NameStringSchema = OptionalStringSchema.refine(x => !x || testName(x), { message: t('message.validation.invalidName') });
export const RequiredNameStringSchema = RequiredStringSchema.refine(x => testName(x), { message: t('message.validation.invalidName') })

const testPhoneNumber = (str: string | null | undefined): boolean => {
    const phoneReplace = /\D/g;
    const cleaned = str ? str.trim().replace(phoneReplace, '') : null;
    return (!!cleaned && (cleaned.length === 10 || (cleaned[0] === '1' && cleaned.length === 11)))
}
export const PhoneNumberSchema = OptionalStringSchema.refine(x => !x || testPhoneNumber(x), { message: t('message.validation.invalidPhone') })
export const RequiredPhoneNumberSchema = RequiredStringSchema.refine(x => testPhoneNumber(x), { message: t('message.validation.invalidPhone') })

export const AdministrativePersonNameSchema = z.object({
    nameUse: z.nativeEnum(NameUse).optional(),
    familyName: NameStringSchema,
    givenName: NameStringSchema,
    middleName: NameStringSchema,
    suffix: NameStringSchema,
    periodStart: z.string().nullish(),
    periodEnd: z.string().nullish(),
});

export const PatientNameSchema = AdministrativePersonNameSchema.extend({
    familyName: RequiredNameStringSchema,
    givenName: RequiredNameStringSchema,
});

const AdminContactPoint = z.object({
    id: z.number().nullish(),
    contactPointSystem: z.nativeEnum(ContactPointSystem),
    contactPointUse: z.nativeEnum(ContactPointUse).nullish(),
    rank: z.number().nullish(),
    value: z.string().nullish()
})

export const RequiredAdministrativeContactPointSchema = AdminContactPoint
    .extend({
        value: z.string()
    })
    .superRefine(({contactPointSystem, value}, ctx) => {
        if ((contactPointSystem === ContactPointSystem.Phone || contactPointSystem === ContactPointSystem.SMS)) {
            const result = RequiredPhoneNumberSchema.safeParse(value);
            if (!result.success) ctx.addIssue(result.error.issues?.[0])
        }
    });

export const AdministrativeContactPointSchema = AdminContactPoint.superRefine(({contactPointSystem, value}, ctx) => {
    if (!!value && (contactPointSystem === ContactPointSystem.Phone || contactPointSystem === ContactPointSystem.SMS)) {
        const result = PhoneNumberSchema.safeParse(value);
        if (!result.success) ctx.addIssue(result.error.issues?.[0])
    }
});

export const PostalCodeSchema = z.string().nullish().or(z.literal(''))
    .refine(v => !v || (!!v && /^\d{5}(-?\d{4})?$/.test(v)), {
        message: t('message.validation.invalidZip')
    });

export const AddressSchema = z.object({
    id: z.number().nullish(),
    patientDemographicsId: z.number().nullish(),
    addressUse: z.nativeEnum(AddressUse).optional(),
    addressType: z.nativeEnum(AddressType).optional(),
    line1: z.string().nullish(),
    line2: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    district: z.string().nullish(),
    postalCode: PostalCodeSchema,
    country: z.string().nullish(),
});

export const RequiredAddressSchema = z.object({
    id: z.number().nullish(),
    patientDemographicsId: z.number().nullish(),
    addressUse: z.nativeEnum(AddressUse).optional(),
    addressType: z.nativeEnum(AddressType).optional(),
    line1: RequiredStringSchema,
    line2: z.string().nullish(),
    city: RequiredStringSchema,
    state: RequiredStringSchema,
    district: z.string().nullish(),
    postalCode: RequiredStringSchema.and(PostalCodeSchema),
    country: z.string().nullish(),
});

export const PatientContactSchema = z.object({
    id: z.number().nullish(),
    patientDemographicsId: z.number().nullish(),
    patientCoverageId: z.number().nullish(),
    relationships: z.array(z.any()).optional(),
    relationshipDescription: OptionalStringSchema,
    nameId: z.number().optional(),
    name: AdministrativePersonNameSchema.optional(),
    birthDate: DateSchema,
    telecom: z.array(AdministrativeContactPointSchema.nullish()),
    addressId: z.number().optional(),
    address: AddressSchema,
    administrativeGenderId: z.number().nullish(),
    administrativeGender: z.any(),
    periodStart: z.string().nullish(),
    periodEnd: z.string().nullish(),
});

const ScalarValueSchema = z.number().int().nonnegative().max(MAX_32_BIT_SIGNED_INT).safe();
const OptionalScalarValueSchema = ScalarValueSchema.nullish();
export const PatientPromptSchema = z.object({
    freeFormResponseValue: z.string().nullish(),
    dateResponseValue: DateSchema,
    booleanResponseValue: z.boolean().nullish(),
    scalarResponseValue: OptionalScalarValueSchema,
    optionResponseValueId: z.number().nullish(),
    optionResponseValue: z.any(),
    mediaResponseMimeType: z.string().nullish(),
    mediaResponseValue: z.string().nullish()
});

export const WorkflowContentPatientPromptSchema = z.object({
    patientPrompt: PatientPromptSchema,
    workflowContent: z.object({
        workflowContentPromptType: z.nativeEnum(WorkflowContentPromptType).nullish(),
        workflowContentPromptOptions: z.array(z.any()).nullish(),
        requiredPrompt: z.boolean().nullish()
    })
}).superRefine((val, ctx: z.RefinementCtx) => {
    if (val.workflowContent.requiredPrompt && val.workflowContent.workflowContentPromptType === WorkflowContentPromptType.Date) {
        const parseResult = RequiredDateSchema.safeParse(val.patientPrompt.dateResponseValue);
        if (!parseResult.success) {
            ctx.addIssue({
                message: parseResult.error.issues.some(x => x.code === 'invalid_type')
                    ? t('message.validation.fieldRequired')
                    : t('message.validation.invalidDate'),
                code: 'custom',
                path: ['patientPrompt', 'dateResponseValue']
            })
        }
    }
    
    if (val.workflowContent.workflowContentPromptType === WorkflowContentPromptType.SingleOptionOther && val.workflowContent.requiredPrompt) {
        const otherOption = val.workflowContent.workflowContentPromptOptions?.find(x => x.title?.toLowerCase()?.includes('other') || x.title?.toLowerCase()?.includes('something else'));
        let parseResult;
        if (otherOption && val.patientPrompt.optionResponseValueId === otherOption?.id) {
            parseResult = val.workflowContent.requiredPrompt
                ? RequiredStringSchema.safeParse(val.patientPrompt.freeFormResponseValue)
                : OptionalStringSchema.safeParse(val.patientPrompt.freeFormResponseValue)

        } else {
            parseResult = val.workflowContent.requiredPrompt
                ? DropDownValueSchema.safeParse(val.patientPrompt.optionResponseValueId)
                : { success: true }
        }

        if (!parseResult.success && !!parseResult.error) {
            ctx.addIssue({
                message: t('message.validation.fieldRequired'),
                code: 'custom',
                path: ['patientPrompt', 'optionResponseValueId']
            });

            ctx.addIssue({
                message: t('message.validation.fieldRequired'),
                code: 'custom',
                path: ['patientPrompt', 'freeFormResponseValue']
            });
        }
    }

    if (val.workflowContent.requiredPrompt && val.workflowContent.workflowContentPromptType === WorkflowContentPromptType.SingleOption) {
        const parseResult = DropDownValueSchema.safeParse(val.patientPrompt.optionResponseValueId)
        if (!parseResult.success){
            ctx.addIssue({
                message:  t('message.validation.fieldRequired'),
                code: 'custom',
                path: ['patientPrompt', 'optionResponseValueId']
            });
        }
    }

    if (val.workflowContent.workflowContentPromptType === WorkflowContentPromptType.Scalar || val.workflowContent.workflowContentPromptType === WorkflowContentPromptType.NetPromoterScore) {
        let parseResult;
        if (val.workflowContent.workflowContentPromptType === WorkflowContentPromptType.Scalar)
            parseResult = val.workflowContent.requiredPrompt
                ? ScalarValueSchema.safeParse(val.patientPrompt.scalarResponseValue)
                : OptionalScalarValueSchema.safeParse(val.patientPrompt.scalarResponseValue)
        else 
            parseResult = val.workflowContent.requiredPrompt
                ? ScalarValueSchema.lte(10).gte(1).safeParse(val.patientPrompt.scalarResponseValue)
                : ScalarValueSchema.lte(10).gte(1).nullish().safeParse(val.patientPrompt.scalarResponseValue)

        if (!parseResult.success) {
            ctx.addIssue({
                message: parseResult.error.issues.some(x => x.code === 'invalid_type')
                    ? t('message.validation.fieldRequired')
                    : t('message.validation.mustBeNumber'),
                code: 'custom',
                path: ['patientPrompt', 'scalarResponseValue']
            });
        }
    }
    if(val.workflowContent.requiredPrompt && (val.workflowContent.workflowContentPromptType === WorkflowContentPromptType.Freeform)){
        const parseResult = RequiredStringSchema.safeParse(val.patientPrompt.freeFormResponseValue)
        if (!parseResult.success){
            ctx.addIssue({
                message:  t('message.validation.fieldRequired'),
                code: 'custom',
                path: ['patientPrompt', 'freeFormResponseValue']
            });
        }
    }
    if(val.workflowContent.requiredPrompt && (val.workflowContent.workflowContentPromptType === WorkflowContentPromptType.Boolean)){
        const parseResult = z.boolean().safeParse(val.patientPrompt.booleanResponseValue)
        if (!parseResult.success){
            ctx.addIssue({
                message:  t('message.validation.fieldRequired'),
                code: 'custom',
                path: ['patientPrompt', 'booleanResponseValue']
            });
        }
    }
});

z.setErrorMap((issue, ctx) => {
    const fieldNameMapping: { [key: string]: string } = {
        givenName: 'First Name',
        familyName: 'Last Name',
        birthDate: 'Date of Birth',
        dateOfBirth: 'Date of Birth',
        postalCode: 'Zip Code',
        previousPostalCode: 'Zip Code',
        scalarResponseValue: 'Value',

        contactGroupName: 'Contact Group Name',
        assignByStatus: 'Status',
        npsScoreGroup: 'NPS Score',
        ageRangeMin: 'Min Age',
        ageRangeMax: 'Max Age',
        facilityLocationId: 'Location',
        autoAssignByCaseTag: 'Assign By Case Tag',
        patientStatus: 'Patient Status',
        serviceType: 'Service Type',
        
        // workflow content validations
        textSegmentBody: 'Text Content'
    }

    const field = issue.path[issue.path.length - 1]?.toString() ?? '';
    if ((issue.code === z.ZodIssueCode.too_small || issue.code === z.ZodIssueCode.invalid_type) && field !== 'scalarResponseValue' && !field.includes('scalarTrigger')) {
        const fieldName = fieldNameMapping[field]
        if (fieldName) {
            return { message: t('message.validation.fieldNameRequired', fieldName) }
        }

        return { message: t('message.validation.fieldRequired') };
    }
    return { message: ctx.defaultError };
});